import {
  normalizeCanvasStrokeWidth,
  resolveConnectorLocalEndpoints,
  type ConnectorNodeProps,
} from "@/pages/canvas/types/canvasNodeData";
import {
  buildLinePath,
  settleStepPoints,
  type LineGeometry,
  type LinePoint,
} from "@/pages/canvas/utils/linePath";
import { Position, useReactFlow, type XYPosition } from "@xyflow/react";
import { AnimatePresence } from "motion/react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { CANVAS_SNAP, snapToGrid } from "../canvas-controls.config";
import { useCanvasApi } from "../context/CanvasContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { useNodeCanEdit, useShowUnlockToolbar } from "../hooks/useNodeCanEdit";
import {
  LineFloatingToolbar,
  type LineStyleFields,
} from "./LineFloatingToolbar";
import type { ArrowEndStyle } from "./shared/ArrowEndPicker";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";
import { CanvasUnlockToolbar } from "./shared/CanvasUnlockToolbar";
import { LineLabelEditor } from "./shared/LineLabel";
import { LineRouteHandles } from "./shared/LineRouteHandles";

// Arrowhead marker dimensions
const MARKER_SIZE = 8;

/** Padding added around the connector bounding box so near-horizontal/vertical
 *  connectors have a reasonable node size and markers are never clipped. */
// Matches useConnectorPlacement's PAD and the backend's nodePad — one pad
// everywhere, and a lattice multiple so endpoint edits keep the box on-grid.
const NODE_PAD = 16;

function sideToward(x: number, y: number, tx: number, ty: number): Position {
  const dx = tx - x;
  const dy = ty - y;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0 ? Position.Right : Position.Left;
  }
  return dy >= 0 ? Position.Bottom : Position.Top;
}

export const ConnectorNode = memo(function ConnectorNode({
  id,
  data,
  selected,
  dragging,
  width = 150,
  height = 1,
  positionAbsoluteX,
  positionAbsoluteY,
}: ConnectorNodeProps) {
  const canvas = useCanvasApi();
  const canEdit = useNodeCanEdit(id);
  const showUnlockToolbar = useShowUnlockToolbar(id, selected, dragging);
  const { screenToFlowPosition } = useReactFlow();
  const [snapX, snapY] = CANVAS_SNAP.grid;

  const {
    start: { x: x1, y: y1 },
    end: { x: x2, y: y2 },
  } = resolveConnectorLocalEndpoints(data, width, height);
  const color =
    data.color && data.color !== "default"
      ? data.color
      : "hsl(var(--foreground) / 0.7)";
  const strokeWidth = normalizeCanvasStrokeWidth(data.stroke_width);
  const strokeStyle = data.stroke_style ?? "solid";
  // Migrate legacy arrowMode → startArrow/endArrow
  const legacyArrow = data.arrow_mode;
  const startArrow: ArrowEndStyle =
    data.start_arrow ?? (legacyArrow === "both" ? "arrow" : "none");
  const endArrow: ArrowEndStyle =
    data.end_arrow ?? (legacyArrow === "none" ? "none" : "arrow");
  const curveType = data.curve_type ?? "straight";

  const strokeDasharray =
    strokeStyle === "dashed"
      ? "16 8"
      : strokeStyle === "dotted"
        ? "3 8"
        : undefined;

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const isPopoverOpenRef = useRef(false);

  // The label rides the path's midpoint; measured off the rendered path so it
  // holds for every curve type.
  const labelText = data.label ?? "";
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  // Live route while a grip is dragged; storage gets the release.
  const [liveBend, setLiveBend] = useState<XYPosition | null | undefined>();
  const showLabel = labelText !== "" || isEditingLabel;
  const [livePoints, setLivePoints] = useState<LinePoint[] | undefined>();

  const handlePopoverOpenChange = useCallback((open: boolean) => {
    isPopoverOpenRef.current = open;
    setIsPopoverOpen(open);
  }, []);

  // Dragging an endpoint — tracks which endpoint is active
  const draggingEndpoint = useRef<"start" | "end" | null>(null);
  const screenToFlowRef = useRef<
    ((p: { x: number; y: number }) => { x: number; y: number }) | null
  >(null);

  screenToFlowRef.current = screenToFlowPosition;

  const startEndpointDrag = useCallback(
    (endpoint: "start" | "end", e: React.PointerEvent) => {
      if (!canEdit) return;
      e.stopPropagation();
      e.preventDefault();
      draggingEndpoint.current = endpoint;

      const onMove = (me: PointerEvent) => {
        if (!draggingEndpoint.current || !screenToFlowRef.current) return;
        const pos = screenToFlowRef.current({ x: me.clientX, y: me.clientY });
        // position in local node coordinates
        const lx = snapToGrid(pos.x - positionAbsoluteX, snapX);
        const ly = snapToGrid(pos.y - positionAbsoluteY, snapY);

        // Update visual immediately via DOM — React will catch up on commit
        if (draggingEndpoint.current === "start") {
          liveStart.current = { x: lx, y: ly };
        } else {
          liveEnd.current = { x: lx, y: ly };
        }
        forceUpdateRef.current((n) => n + 1);
      };

      const onUp = (ue: PointerEvent) => {
        if (!draggingEndpoint.current || !screenToFlowRef.current) return;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);

        const pos = screenToFlowRef.current({ x: ue.clientX, y: ue.clientY });
        const finalAbsX = snapToGrid(pos.x, snapX);
        const finalAbsY = snapToGrid(pos.y, snapY);

        // Compute new absolute positions of both endpoints
        const absX1 =
          draggingEndpoint.current === "start"
            ? finalAbsX
            : positionAbsoluteX + x1;
        const absY1 =
          draggingEndpoint.current === "start"
            ? finalAbsY
            : positionAbsoluteY + y1;
        const absX2 =
          draggingEndpoint.current === "end"
            ? finalAbsX
            : positionAbsoluteX + x2;
        const absY2 =
          draggingEndpoint.current === "end"
            ? finalAbsY
            : positionAbsoluteY + y2;

        const newMinX = Math.min(absX1, absX2);
        const newMinY = Math.min(absY1, absY2);
        const newW = Math.max(Math.abs(absX2 - absX1), 1) + NODE_PAD * 2;
        const newH = Math.max(Math.abs(absY2 - absY1), 1) + NODE_PAD * 2;

        // Final local coords (offset by NODE_PAD so endpoints sit inside the padded box)
        const finalX1 = absX1 - newMinX + NODE_PAD;
        const finalY1 = absY1 - newMinY + NODE_PAD;
        const finalX2 = absX2 - newMinX + NODE_PAD;
        const finalY2 = absY2 - newMinY + NODE_PAD;

        // Keep live refs at the committed values until node props catch up.
        // Clearing them on pointer-up would flash back to stale data for a
        // frame while Liveblocks reconciles and React Flow re-renders.
        liveStart.current = { x: finalX1, y: finalY1 };
        liveEnd.current = { x: finalX2, y: finalY2 };
        const committed = {
          start: { x: finalX1, y: finalY1 },
          end: { x: finalX2, y: finalY2 },
        };
        pendingCommit.current = committed;

        canvas.updateConnectorEndpoints(
          id,
          committed,
          { x: newMinX - NODE_PAD, y: newMinY - NODE_PAD },
          { width: newW, height: newH },
        );

        draggingEndpoint.current = null;
        // Do NOT clear liveStart/liveEnd here — cleared once props match pendingCommit.
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [
      canEdit,
      id,
      canvas,
      positionAbsoluteX,
      positionAbsoluteY,
      x1,
      y1,
      x2,
      y2,
      snapX,
      snapY,
    ],
  );

  // Optimistic endpoint coords during drag and until storage round-trips to props.
  const liveStart = useRef<{ x: number; y: number } | null>(null);
  const liveEnd = useRef<{ x: number; y: number } | null>(null);
  // Triggers re-renders during pointer drag so the SVG updates in real time.
  const [, forceUpdate] = useState(0);
  const forceUpdateRef = useRef(forceUpdate);
  forceUpdateRef.current = forceUpdate;
  // Last committed endpoints — cleared once node data matches (post-Liveblocks sync).
  const pendingCommit = useRef<{
    start: { x: number; y: number };
    end: { x: number; y: number };
  } | null>(null);

  // Once props reflect the commit, drop the optimistic override (avoids a flash).
  if (pendingCommit.current) {
    const synced = resolveConnectorLocalEndpoints(data, width, height);
    if (
      synced.start.x === pendingCommit.current.start.x &&
      synced.start.y === pendingCommit.current.start.y &&
      synced.end.x === pendingCommit.current.end.x &&
      synced.end.y === pendingCommit.current.end.y
    ) {
      liveStart.current = null;
      liveEnd.current = null;
      pendingCommit.current = null;
    }
  }

  // Resolved positions: live override while dragging or awaiting prop sync
  const rx1 = liveStart.current ? liveStart.current.x : x1;
  const ry1 = liveStart.current ? liveStart.current.y : y1;
  const rx2 = liveEnd.current ? liveEnd.current.x : x2;
  const ry2 = liveEnd.current ? liveEnd.current.y : y2;

  // Each end faces the other along the dominant axis, the way an attached
  // edge's ends do, so the bezier and elbow shaping match.
  const geometry: LineGeometry = {
    curve: curveType,
    source: { x: rx1, y: ry1, side: sideToward(rx1, ry1, rx2, ry2) },
    target: { x: rx2, y: ry2, side: sideToward(rx2, ry2, rx1, ry1) },
    bend: liveBend !== undefined ? liveBend : (data.bend ?? null),
    points: livePoints ?? (data.points as LinePoint[] | undefined) ?? null,
    labelled: labelText !== "",
  };
  const built = buildLinePath(geometry);
  const livePath = built.path;
  const labelPoint = built.label;

  useEffect(() => {
    if (!selected) setIsEditingLabel(false);
  }, [selected]);

  const handleStyleChange = useCallback(
    (style: LineStyleFields) => {
      canvas.updateNodeData(id, {
        ...(style as Record<string, unknown>),
        // A change of curve drops a route drawn for the old curve;
        // re-picking the current curve is not a change.
        ...(style.curve_type && style.curve_type !== curveType
          ? { bend: null, points: null }
          : {}),
      });
    },
    [id, canvas, curveType],
  );

  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();

  // Multi-select unmounts this toolbar without Radix onOpenChange(false).
  // Deselect must also clear the latch — otherwise isPopoverOpen alone would
  // keep the toolbar mounted after a canvas click.
  useEffect(() => {
    if (!isPopoverOpen) return;
    if (isMultiSelectToolbarOpen || !selected) {
      handlePopoverOpenChange(false);
    }
  }, [
    isMultiSelectToolbarOpen,
    selected,
    isPopoverOpen,
    handlePopoverOpenChange,
  ]);

  // Toolbar follows selection only — do not keep it mounted via isPopoverOpen
  // after deselection.
  // Suppress when MultiSelectFloatingToolbar is mounted (multiSelectNodes >= 2).
  const showToolbar =
    canEdit &&
    !dragging &&
    !isMultiSelectToolbarOpen &&
    selected &&
    selectedNodeCount === 1;

  // SVG viewport: give generous padding so stroke + marker are never clipped
  const PAD = MARKER_SIZE + strokeWidth + 2;
  const svgW = width + PAD * 2;
  const svgH = height + PAD * 2;

  // Marker IDs (unique per node to avoid cross-contamination)
  const markerEndId = `ce-${id}`;
  const markerStartId = `cs-${id}`;

  return (
    <div
      style={{ width, height, position: "relative", pointerEvents: "none" }}
      className="overflow-visible"
    >
      {showUnlockToolbar && (
        <CanvasUnlockToolbar
          nodeIds={[id]}
          positionAbsoluteX={positionAbsoluteX}
          positionAbsoluteY={
            positionAbsoluteY + Math.min(0, built.top, built.label.y)
          }
          width={width}
          topOffset={64}
        />
      )}

      <AnimatePresence>
        {showToolbar && !isEditingLabel && (
          <CanvasFloatingToolbarPortal
            positionAbsoluteX={positionAbsoluteX}
            // A bend or a label can rise above the ends' box; the bar clears
            // whichever is highest, as an edge's does.
            positionAbsoluteY={
              positionAbsoluteY + Math.min(0, built.top, built.label.y)
            }
            width={width}
            topOffset={64}
          >
            <LineFloatingToolbar
              variant="connector"
              style={{
                color,
                stroke_width: strokeWidth,
                stroke_style: strokeStyle,
                start_arrow: startArrow,
                end_arrow: endArrow,
                curve_type: curveType,
              }}
              onStyleChange={handleStyleChange}
              onPopoverOpenChange={handlePopoverOpenChange}
              hasLabel={labelText !== ""}
              onEditLabel={() => setIsEditingLabel(true)}
            />
          </CanvasFloatingToolbarPortal>
        )}
      </AnimatePresence>

      <svg
        width={svgW}
        height={svgH}
        style={{
          position: "absolute",
          left: -PAD,
          top: -PAD,
          overflow: "visible",
          pointerEvents: "none",
        }}
      >
        <defs>
          {" "}
          {endArrow !== "none" && (
            <marker
              id={markerEndId}
              markerWidth={MARKER_SIZE}
              markerHeight={MARKER_SIZE}
              refX={MARKER_SIZE - 1}
              refY={MARKER_SIZE / 2}
              orient="auto"
            >
              {endArrow === "arrow-open" ? (
                <polyline
                  points={`1,1 ${MARKER_SIZE - 1},${MARKER_SIZE / 2} 1,${MARKER_SIZE - 1}`}
                  fill="none"
                  stroke={color}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : (
                <path
                  d={`M 0 0 L ${MARKER_SIZE} ${MARKER_SIZE / 2} L 0 ${MARKER_SIZE} Z`}
                  fill={color}
                />
              )}
            </marker>
          )}
          {startArrow !== "none" && (
            <marker
              id={markerStartId}
              markerWidth={MARKER_SIZE}
              markerHeight={MARKER_SIZE}
              refX={MARKER_SIZE - 1}
              refY={MARKER_SIZE / 2}
              orient="auto-start-reverse"
            >
              {startArrow === "arrow-open" ? (
                <polyline
                  points={`1,1 ${MARKER_SIZE - 1},${MARKER_SIZE / 2} 1,${MARKER_SIZE - 1}`}
                  fill="none"
                  stroke={color}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : (
                <path
                  d={`M 0 0 L ${MARKER_SIZE} ${MARKER_SIZE / 2} L 0 ${MARKER_SIZE} Z`}
                  fill={color}
                />
              )}
            </marker>
          )}
        </defs>

        {/* Invisible wide hit area for easier selection and dragging.
            The connector-drag-handle class restricts React Flow dragging to
            this element only — the empty bounding box is not draggable. */}
        <path
          className="connector-drag-handle"
          d={livePath}
          transform={`translate(${PAD}, ${PAD})`}
          stroke="transparent"
          strokeWidth={Math.max(strokeWidth + 12, 16)}
          fill="none"
          style={{ pointerEvents: "stroke", cursor: "pointer" }}
          onDoubleClick={(e) => {
            // Double-click the line to label it, as on an edge.
            e.stopPropagation();
            if (canEdit) setIsEditingLabel(true);
          }}
        />

        {/* Visible path */}
        <path
          d={livePath}
          transform={`translate(${PAD}, ${PAD})`}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          markerEnd={endArrow !== "none" ? `url(#${markerEndId})` : undefined}
          markerStart={
            startArrow !== "none" ? `url(#${markerStartId})` : undefined
          }
        />

        {/* Endpoint handles — only visible when selected */}
        {selected && canEdit && !isEditingLabel && (
          <>
            <EndpointHandle
              cx={rx1 + PAD}
              cy={ry1 + PAD}
              color={color}
              onPointerDown={(e) => startEndpointDrag("start", e)}
            />
            <EndpointHandle
              cx={rx2 + PAD}
              cy={ry2 + PAD}
              color={color}
              onPointerDown={(e) => startEndpointDrag("end", e)}
            />
            <g transform={`translate(${PAD}, ${PAD})`}>
              <LineRouteHandles
                geometry={geometry}
                built={built}
                origin={{ x: positionAbsoluteX, y: positionAbsoluteY }}
                onLineDoubleClick={() => {
                  if (canEdit) setIsEditingLabel(true);
                }}
                onBendChange={(bend, final) => {
                  if (!final) {
                    setLiveBend(bend);
                    return;
                  }
                  setLiveBend(undefined);
                  canvas.updateNodeData(id, { bend });
                }}
                onPointsChange={(points, final) => {
                  if (!final) {
                    setLivePoints(points);
                    return;
                  }
                  setLivePoints(undefined);
                  canvas.updateNodeData(id, {
                    points: settleStepPoints(geometry, points),
                  });
                }}
              />
            </g>
          </>
        )}
      </svg>

      {showLabel && (
        <div
          className="nodrag nopan"
          style={{
            position: "absolute",
            left: labelPoint.x,
            top: labelPoint.y,
            transform: "translate(-50%, -50%)",
            pointerEvents: "all",
          }}
        >
          <LineLabelEditor
            label={labelText}
            labelStyle={data.label_style}
            editing={isEditingLabel}
            canEdit={canEdit}
            midpoint={{
              x: positionAbsoluteX + labelPoint.x,
              y: positionAbsoluteY + labelPoint.y,
            }}
            onStartEdit={() => setIsEditingLabel(true)}
            onEndEdit={() => setIsEditingLabel(false)}
            onCommit={(label) => canvas.updateNodeData(id, { label })}
            onStyleChange={(label_style) =>
              canvas.updateNodeData(id, { label_style })
            }
          />
        </div>
      )}
    </div>
  );
});

interface EndpointHandleProps {
  cx: number;
  cy: number;
  color: string;
  onPointerDown: (e: React.PointerEvent<SVGCircleElement>) => void;
}

function EndpointHandle({ cx, cy, color, onPointerDown }: EndpointHandleProps) {
  return (
    <circle
      cx={cx}
      cy={cy}
      r={5}
      fill="hsl(var(--background))"
      stroke={color}
      strokeWidth={2}
      style={{ pointerEvents: "all", cursor: "crosshair" }}
      onPointerDown={onPointerDown}
    />
  );
}
